import { Country } from 'country-state-city';
import { EAddressType } from '@xeppt/xeppt-sdk/types/user';
import {
    ETransactionAction,
    ETransactionMethod,
    ETransactionStatus,
    ETransactionType
} from '@xeppt/xeppt-sdk/types/transaction';
import * as XLSX from 'xlsx';
import { EConsumerStatus, EVerificationStatus } from '@xeppt/xeppt-sdk/types/consumer';
import { EGiftCardStatus } from '@xeppt/xeppt-sdk/types/gift_cards';
import {
    ECardStatus,
    ECardType,
    ELogLevel,
    ETicketStatus,
    EUserStatus,
    TTicketMessage,
    EPaymentIntentStatus
} from '@xeppt/xeppt-sdk/types';
import moment from 'moment-timezone';

export const highlightSearchText = (text: string, search: string): string => {
    if (!search) return text; // If there's no search query, return the original text

    const regex = new RegExp(`(${search})`, 'gi'); // Case-insensitive match for the search term
    return text.replace(regex, `<i>$1</i>`); // Wrap matching text in <i> tags
};

export const getFirstLetter = (string?: string) => {
    if (!string) return '';
    return string.charAt(0);
};

export const hidecard_number = (
    value: number | string,
    type?: 'full' | 'partial',
    variant?: 'dots' | 'stars'
) => {
    const separator = variant === 'dots' ? '••••' : '****';
    const string = String(value);
    if (type === 'full') {
        return `${separator} ${string.slice(12)}`;
    } else {
        return `${string.slice(0, 4)} ${separator} ${separator} ${string.slice(12)}`;
    }
};

export const capitalizeFirstLetter = (string?: string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const hidephone_number = (value: string | number) => {
    const str = String(value);
    return (
        str
            .substring(0, str.length - 4)
            .split('')
            .map(() => '•')
            .join('') + str.substring(str.length - 4, str.length)
    );
};

export const joiningArrayWords = (array: string[]) => {
    if (array.length === 0) {
        return '';
    } else if (array.length === 1) {
        return array[0];
    } else {
        const joinedFormats = array.slice(0, -1).join(', ');
        return `${joinedFormats} or ${array[array.length - 1]}`;
    }
};

export const getBirthDisabledDates = (): Date => {
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - 18);

    return maxDate;
};

export const prettifyGoogleAddress = ({ address_components }: any) => {
    let streetName = '';
    let streetNumber = '';
    const address = {
        country: '',
        city: '',
        address_1: '',
        address_2: '',
        zip_code: '',
        region: ''
    };

    for (const component of address_components) {
        if (component.types.includes('street_number')) {
            streetNumber = component.long_name;
        }
        if (component.types.includes('route')) {
            streetName = component.long_name;
        }
        if (component.types.includes('locality')) {
            address['city'] = component.long_name;
        }
        if (component.types.includes('country')) {
            const country = Country.getCountryByCode(component.short_name);
            address['country'] = country?.name || component.short_name;
        }
        if (component.types.includes('postal_code')) {
            address['zip_code'] = component.long_name;
        }
        if (component.types.includes('administrative_area_level_1')) {
            address['region'] += component.long_name;
        }
    }
    if (
        address.country === '' ||
        address.city === '' ||
        streetName === '' ||
        streetNumber === '' ||
        address.zip_code === '' ||
        address.region === ''
    ) {
        return undefined;
    } else {
        return {
            ...address,
            address_1: streetNumber + ' ' + streetName
        };
    }
};

export const getphone_codeFromIso = (isoCode: string) => {
    return Country.getCountryByCode(isoCode)?.phonecode || '1';
};

export const getAddressFormName = (type: EAddressType) => {
    if (type === EAddressType.BUSINESS) {
        return 'business_address';
    } else if (type === EAddressType.REGISTRATION) {
        return 'registration_address';
    } else {
        return 'trading_address';
    }
};
export const getAddressName = (type: EAddressType, localization: (val: string) => string) => {
    if (type === EAddressType.BUSINESS) {
        return localization('business');
    } else if (type === EAddressType.REGISTRATION) {
        return localization('registration');
    } else {
        return localization('trading');
    }
};

export const getTransactionAction = (action?: ETransactionAction): string => {
    switch (action) {
        case ETransactionAction.DEPOSIT:
            return 'Deposit';
        case ETransactionAction.WITHDRAW:
            return 'Withdraw';
        case ETransactionAction.TRANSFER:
            return 'Transfer';
        case ETransactionAction.PAYMENT:
            return 'Payment';
        case ETransactionAction.REFUND:
            return 'Refund';
        case ETransactionAction.CASHBACK:
            return 'Cashback';
        case ETransactionAction.CARD_DEPOSIT:
            return 'Card deposit';
        case ETransactionAction.CARD_SERVICE:
            return 'Card service';
        case ETransactionAction.REQUEST:
            return 'Request';
        case ETransactionAction.CARD_TANSACTION:
            return 'Card purchase';
        case ETransactionAction.GIFT_CARD:
            return 'Issue gift card';
        case ETransactionAction.GIFT_CARD_REDEEM:
            return 'Redeem gift card';
        case ETransactionAction.GIFT_CARD_RESERVE:
            return 'Reserve gift card';
    }

    return '';
};

export const getGiftCardStatusString = (action?: EGiftCardStatus) => {
    switch (action) {
        case EGiftCardStatus.ACTIVE:
            return 'Active';
        case EGiftCardStatus.CANCELLED:
            return 'Cancelled';
        case EGiftCardStatus.CANCEL_REQUESTED:
            return 'Cancel requested';
        case EGiftCardStatus.ISSUED:
            return 'Issued';
        case EGiftCardStatus.PARTIALLY_REDEEMED:
            return 'Partially redeemed';
        case EGiftCardStatus.PENDING:
            return 'Pending';
        case EGiftCardStatus.REDEEMED:
            return 'Redeemed';
        default:
            return '';
    }
};

export const getGiftCardsStatusColor = (status?: EGiftCardStatus) => {
    switch (status) {
        case EGiftCardStatus.ACTIVE:
            return 'success';
        case EGiftCardStatus.CANCELLED:
        case EGiftCardStatus.CANCEL_REQUESTED:
            return 'error';
        case EGiftCardStatus.ISSUED:
            return 'blue';
        case EGiftCardStatus.PARTIALLY_REDEEMED:
            return 'lightblue';
        case EGiftCardStatus.REDEEMED:
            return 'gray';
        case EGiftCardStatus.PENDING:
            return 'warning';
        default:
            return 'gray';
    }
};

export const getIntentStatusVariant = (status?: EPaymentIntentStatus) => {
    switch (status) {
        case EPaymentIntentStatus.Paid:
            return 'success';
        case EPaymentIntentStatus.Pending:
            return 'warning';
        case EPaymentIntentStatus.Declined:
            return 'error';
        case EPaymentIntentStatus.InProcess:
            return 'blue';
        case EPaymentIntentStatus.Expired:
        case EPaymentIntentStatus.Canceled:
        case EPaymentIntentStatus.Unknown:
            return 'gray';
        default:
            return 'success';
    }
};

export function getPaymentIntentStatusString(status: EPaymentIntentStatus): string {
    switch (status) {
        case EPaymentIntentStatus.Pending:
            return 'Pending';

        case EPaymentIntentStatus.InProcess:
            return 'In process';

        case EPaymentIntentStatus.Paid:
            return 'Paid';

        case EPaymentIntentStatus.Expired:
            return 'Expired';

        case EPaymentIntentStatus.Canceled:
            return 'Canceled';

        case EPaymentIntentStatus.Declined:
            return 'Declined';

        case EPaymentIntentStatus.Unknown:
        default:
            return 'Unknown';
    }
}

export const getTransactionMethod = (action?: ETransactionMethod) => {
    switch (action) {
        case ETransactionMethod.E_TRANSFER:
            return 'E-transfer';
        case ETransactionMethod.EFT:
            return 'Bank';
        case ETransactionMethod.WALLET:
            return 'Wallet';
        case ETransactionMethod.PAY_BILL:
            return 'Bill payment';
        case ETransactionMethod.EPS:
            return 'Linked card';
        case ETransactionMethod.CARD:
            return 'Prepaid card';
        case ETransactionMethod.GIFT_CARD:
            return 'Gift card';
        default:
            return '';
    }
};

export const getTransactionStatusColor = (status?: ETransactionStatus) => {
    switch (status) {
        case ETransactionStatus.APPROVED:
            return 'success';
        case ETransactionStatus.PENDING:
            return 'warning';
        case ETransactionStatus.DECLINED:
        case ETransactionStatus.CANCELED:
        case ETransactionStatus.REFUND:
        case ETransactionStatus.ERROR:
            return 'error';
        default:
            return 'success';
    }
};

export const getLogsStatusColor = (status?: ELogLevel) => {
    switch (status) {
        case ELogLevel.INFO:
            return 'success';
        case ELogLevel.WARNING:
            return 'warning';
        case ELogLevel.ERROR:
            return 'error';
        default:
            return 'success';
    }
};

export const getConsumerStatusColor = (status?: EConsumerStatus) => {
    switch (status) {
        case EConsumerStatus.ACTIVE:
            return 'success';
        case EConsumerStatus.PENDING:
            return 'warning';
        case EConsumerStatus.UNVERIFIED:
        case EConsumerStatus.UNCONFIRMED:
        case EConsumerStatus.BLOCKED:
        case EConsumerStatus.SUSPENDED:
            return 'error';
        default:
            return 'success';
    }
};

export const getAdminStatusColor = (status?: EUserStatus) => {
    switch (status) {
        case EUserStatus.ACTIVE:
            return 'success';
        case EUserStatus.PENDING:
            return 'warning';
        case EUserStatus.UNVERIFIED:
        case EUserStatus.UNCONFIRMED:
        case EUserStatus.BLOCKED:
        case EUserStatus.SUSPENDED:
            return 'error';
        default:
            return 'success';
    }
};

export const getTransactionStatus = (status?: ETransactionStatus) => {
    switch (status) {
        case ETransactionStatus.APPROVED:
            return 'Approved';
        case ETransactionStatus.PENDING:
            return 'Pending';
        case ETransactionStatus.DECLINED:
            return 'Declined';
        case ETransactionStatus.CANCELED:
            return 'Canceled';
        case ETransactionStatus.ERROR:
            return 'Error';
        case ETransactionStatus.REFUND:
            return 'Refunded';
    }
};

export const getLogsLevel = (status?: ELogLevel) => {
    switch (status) {
        case ELogLevel.INFO:
            return 'INFO';
        case ELogLevel.WARNING:
            return 'WARNING';
        case ELogLevel.ERROR:
            return 'ERROR';
    }
};

export const getConsumerStatus = (status?: EConsumerStatus) => {
    switch (status) {
        case EConsumerStatus.ACTIVE:
            return 'Active';
        case EConsumerStatus.PENDING:
            return 'Pending';
        case EConsumerStatus.UNVERIFIED:
            return 'Unverified';
        case EConsumerStatus.UNCONFIRMED:
            return 'Unconfirmed';
        case EConsumerStatus.BLOCKED:
            return 'Blocked';
        case EConsumerStatus.SUSPENDED:
            return 'Suspended';
    }
};

export const getAdminStatus = (status?: EUserStatus) => {
    switch (status) {
        case EUserStatus.ACTIVE:
            return 'Active';
        case EUserStatus.PENDING:
            return 'Verification pending';
        case EUserStatus.UNVERIFIED:
            return 'Unverified';
        case EUserStatus.UNCONFIRMED:
            return 'Unconfirmed';
        case EUserStatus.BLOCKED:
            return 'Blocked';
        case EUserStatus.SUSPENDED:
            return 'Suspended';
    }
};

export const downloadFileFromBlob = (data: any, name: string) => {
    const a = document.createElement('a');
    a.download = name;
    a.href = URL.createObjectURL(data);
    a.addEventListener('click', () => {
        setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
    });
    a.click();
};

export const formatDataToXlsx = (data: any) => {
    const keys: any[] = [];
    data.forEach((item: any) => {
        Object.keys(item).forEach((key) => {
            if (!keys.includes(key)) {
                keys.push(camelize(key));
            }
        });
    });
    const widthArray = keys.map((item) => {
        return {
            wch: data.reduce((w: any, r: any) => Math.max(w, r[item]?.length || 0), 13)
        };
    });
    const fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheethtml.sheet;charset=UTF-8';
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = widthArray;
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { type: fileType });
};

export const camelize = (str: string) => {
    return str
        .replace(/^\w|[A-Z]|\b\w/g, function (word, index) {
            return index === 0 ? word.toUpperCase() : word.toUpperCase();
        })
        .replace(/\s+/g, '');
};

export const removeEmptyArrays = <T>(obj: { [key: string]: any }): T => {
    const result = {};

    for (const key in obj) {
        //eslint-disable-next-line
        if (obj.hasOwnProperty(key)) {
            const value = obj[key];
            if (!(Array.isArray(value) && value.length === 0)) {
                //@ts-ignore
                result[key] = value;
            }
        }
    }

    return result as T;
};

export const getDateOneMonthAgo = () => {
    const currentDate = new Date();
    const pastDate = new Date(currentDate);

    pastDate.setMonth(currentDate.getMonth() - 1);

    return pastDate;
};

export const prettifyCardStatus = (status: ECardStatus) => {
    switch (status) {
        case ECardStatus.ACTIVE:
            return 'Active';
        case ECardStatus.TEMPORARY_CLOSED:
            return 'Inactive';
        case ECardStatus.WAITING_FOR_ACTIVATION:
            return 'Waiting for activation';
        case ECardStatus.LOST:
            return 'Lost';
        case ECardStatus.STOLEN:
            return 'Stolen';
        case ECardStatus.OTHER:
            return 'Other';
        case ECardStatus.FRAUD:
            return 'Fraud';
        case ECardStatus.CUSTOMER_REQUEST:
            return 'Requested';
        case ECardStatus.CLOSED:
            return 'Closed';
        case ECardStatus.LEGAL_CLOSED:
            return 'Legal closed';
        case ECardStatus.FREEZED:
            return 'Frozen';
    }
};
export const snakeToCamel = (str: string): string => {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

export const getTagVariant = (status: ECardStatus) => {
    switch (status) {
        case ECardStatus.ACTIVE:
            return 'success';
        case ECardStatus.OTHER:
        case ECardStatus.WAITING_FOR_ACTIVATION:
        case ECardStatus.CUSTOMER_REQUEST:
            return 'warning';
        case ECardStatus.TEMPORARY_CLOSED:
        case ECardStatus.LOST:
        case ECardStatus.STOLEN:
        case ECardStatus.FRAUD:
        case ECardStatus.CLOSED:
        case ECardStatus.LEGAL_CLOSED:
        case ECardStatus.FREEZED:
            return 'error';
    }
};

export const prettifyCardType = (type?: ECardType) => {
    switch (type) {
        case ECardType.PHYSICAL:
            return 'Primary';
        case ECardType.VIRTUAL:
            return 'Virtual';
        case ECardType.VIRTUAL_SUPPLEMENTARY:
            return 'Virtual Supplementary';
        case ECardType.PHYSICAL_SUPPLEMENTARY:
            return 'Supplementary';
    }
};

export function getDefaultPhoneCode(tz?: string) {
    const countries = Country.getAllCountries();
    const localTimeZone = tz || moment.tz.guess();
    const defaultCountry = Country.getCountryByCode('CA');

    const localCountry = countries.find((country) =>
        country?.timezones?.find((data) => data.zoneName === localTimeZone)
    );

    return (localCountry?.phonecode as string) || (defaultCountry?.phonecode as string);
}

export const getTicketStatusVariant = (status: ETicketStatus) => {
    switch (status) {
        case ETicketStatus.NEW:
            return 'blue';
        case ETicketStatus.HOLD:
            return 'purple';
        case ETicketStatus.OPEN:
            return 'warning';
        case ETicketStatus.PENDING:
            return 'warning';
        case ETicketStatus.SOLVED:
            return 'success';
        case ETicketStatus.CLOSED:
            return 'gray';
        default:
            return 'gray';
    }
};

interface GroupedMessages {
    date: string; // Formatted as DD.MM.YYYY
    messages: TTicketMessage[];
}

export const groupMessagesByDate = (
    messages: TTicketMessage[],
    userTimezone: string // Pass the user's timezone as a parameter
): GroupedMessages[] => {
    const groupedMessages: Record<string, TTicketMessage[]> = {};

    // Group messages by date (adjusted to user's timezone)
    messages.forEach((msg) => {
        const dateKey = moment(msg.created_at).tz(userTimezone).format('MM.DD.YYYY'); // Adjust to user's timezone
        if (!groupedMessages[dateKey]) {
            groupedMessages[dateKey] = [];
        }
        groupedMessages[dateKey].push(msg);
    });

    // Convert groupedMessages into an array, sort messages inside each group, and sort groups by date
    return Object.entries(groupedMessages)
        .map(([date, messages]) => ({
            date,
            messages: messages.sort((a, b) => {
                // Sort messages within the group by `created_at` (oldest to newest)
                const timeA = moment(a.created_at).tz(userTimezone).valueOf();
                const timeB = moment(b.created_at).tz(userTimezone).valueOf();
                return timeA - timeB; // Ascending order (oldest to newest)
            })
        }))
        .sort((a, b) => {
            // Sort groups by `date` in ascending order (oldest group first)
            //@ts-ignore
            const dateA = moment(a.date, 'MM.DD.YYYY').tz(userTimezone).valueOf();
            const dateB = moment(b.date, 'MM.DD.YYYY').tz(userTimezone).valueOf();
            return dateA - dateB; // Ascending order
        });
};

export const convertStringsToNumbers = (obj: Record<string, any>): Record<string, any> => {
    if (Array.isArray(obj)) {
        return obj.map(convertStringsToNumbers);
    }

    if (typeof obj === 'object' && obj !== null) {
        return Object.keys(obj).reduce(
            (acc, key) => {
                const value = obj[key];
                if (typeof value === 'string' && !isNaN(Number(value))) {
                    acc[key] = Number(value);
                } else if (typeof value === 'object') {
                    acc[key] = convertStringsToNumbers(value);
                } else {
                    acc[key] = value;
                }
                return acc;
            },
            {} as Record<string, any>
        );
    }

    return obj;
};

export type TAcceptedFile =
    | '.doc'
    | '.xls'
    | 'image/png'
    | 'image/jpg'
    | 'image/jpeg'
    | '.pdf'
    | '.docx';

export const fileFormatString = {
    '.doc': 'doc',
    '.xls': 'xls',
    'image/png': 'png',
    'image/jpg': 'jpg',
    'image/jpeg': 'jpeg',
    '.pdf': 'pdf',
    '.docx': 'docx'
};

export const formatSnakeToSplit = (string: string) => {
    return string.replaceAll('_', ' ');
};
/**
 * Format a date according to the specified format string
 * @param date - Date object, string, or timestamp
 * @param format - Format string with tokens
 * @param locale - Locale for month/day names (default: 'en-US')
 * @returns Formatted date string
 */
export function formatDate(
    date?: Date | string | number,
    format?: string,
    locale: string = 'en-US'
): string {
    if (!date || !format) return '';
    const d = new Date(date);

    // Validate date
    if (isNaN(d.getTime())) {
        throw new Error('Invalid date provided');
    }

    // Check if date represents midnight in UTC (00:00:00.000Z)
    const isUTCMidnight =
        d.getUTCHours() === 0 &&
        d.getUTCMinutes() === 0 &&
        d.getUTCSeconds() === 0 &&
        d.getUTCMilliseconds() === 0;

    // Use local time (respect timezone) unless date represents UTC midnight
    const useLocalTime = !isUTCMidnight;
    const timeZone = useLocalTime ? undefined : 'UTC';

    // Helper functions to get date parts (local or UTC)
    const getYear = () => (useLocalTime ? d.getFullYear() : d.getUTCFullYear());
    const getMonth = () => (useLocalTime ? d.getMonth() : d.getUTCMonth());
    const getDate = () => (useLocalTime ? d.getDate() : d.getUTCDate());
    const getDay = () => (useLocalTime ? d.getDay() : d.getUTCDay());
    const getHours = () => (useLocalTime ? d.getHours() : d.getUTCHours());
    const getMinutes = () => (useLocalTime ? d.getMinutes() : d.getUTCMinutes());
    const getSeconds = () => (useLocalTime ? d.getSeconds() : d.getUTCSeconds());
    const getMilliseconds = () => (useLocalTime ? d.getMilliseconds() : d.getUTCMilliseconds());

    // Get localized month and weekday names
    const monthsLong = Array.from({ length: 12 }, (_, i) => {
        const referenceDate = useLocalTime ? new Date(2000, i, 1) : new Date(Date.UTC(2000, i, 1));

        return new Intl.DateTimeFormat(locale, {
            month: 'long',
            ...(timeZone && { timeZone })
        }).format(referenceDate);
    });

    const monthsShort = Array.from({ length: 12 }, (_, i) => {
        const referenceDate = useLocalTime ? new Date(2000, i, 1) : new Date(Date.UTC(2000, i, 1));

        return new Intl.DateTimeFormat(locale, {
            month: 'short',
            ...(timeZone && { timeZone })
        }).format(referenceDate);
    });

    const weekdaysLong = Array.from({ length: 7 }, (_, i) => {
        const referenceDate = useLocalTime
            ? new Date(2000, 0, 2 + i)
            : new Date(Date.UTC(2000, 0, 2 + i));

        return new Intl.DateTimeFormat(locale, {
            weekday: 'long',
            ...(timeZone && { timeZone })
        }).format(referenceDate);
    });

    const weekdaysShort = Array.from({ length: 7 }, (_, i) => {
        const referenceDate = useLocalTime
            ? new Date(2000, 0, 2 + i)
            : new Date(Date.UTC(2000, 0, 2 + i));

        return new Intl.DateTimeFormat(locale, {
            weekday: 'short',
            ...(timeZone && { timeZone })
        }).format(referenceDate);
    });

    // Get day of year
    const startOfYear = useLocalTime
        ? new Date(getYear(), 0, 1)
        : new Date(Date.UTC(getYear(), 0, 1));
    const dayOfYear = Math.floor((d.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000)) + 1;

    // Get week of year (ISO week)
    const firstThursday = useLocalTime
        ? new Date(getYear(), 0, 1)
        : new Date(Date.UTC(getYear(), 0, 1));

    if (useLocalTime) {
        firstThursday.setDate(firstThursday.getDate() + ((4 - firstThursday.getDay() + 7) % 7));
    } else {
        firstThursday.setUTCDate(
            firstThursday.getUTCDate() + ((4 - firstThursday.getUTCDay() + 7) % 7)
        );
    }

    const weekOfYear =
        Math.floor((d.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;

    // Get quarter
    const quarter = Math.ceil((getMonth() + 1) / 3);

    // Calculate timezone offset
    let timezoneOffsetHours = 0;
    let timezoneOffsetMinutes = 0;
    let timezoneSign = '+';

    if (useLocalTime) {
        const offsetMinutes = d.getTimezoneOffset();
        timezoneOffsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
        timezoneOffsetMinutes = Math.abs(offsetMinutes) % 60;
        timezoneSign = offsetMinutes <= 0 ? '+' : '-';
    }
    // For UTC midnight, timezone is always +00:00
    // (timezoneOffsetHours and timezoneOffsetMinutes remain 0, timezoneSign remains '+')

    // Get timezone tokens
    const timezoneZ = `${timezoneSign}${String(timezoneOffsetHours).padStart(2, '0')}:${String(timezoneOffsetMinutes).padStart(2, '0')}`;
    const timezoneZZ = `${timezoneSign}${String(timezoneOffsetHours).padStart(2, '0')}${String(timezoneOffsetMinutes).padStart(2, '0')}`;

    // Token definitions - order is crucial, longest patterns first
    const tokenDefinitions = [
        ['YYYY', getYear().toString()],
        ['MMMM', monthsLong[getMonth()]],
        ['dddd', weekdaysLong[getDay()]],
        ['DDDD', String(dayOfYear).padStart(3, '0')],
        ['DDDo', getOrdinal(dayOfYear)],
        ['MMM', monthsShort[getMonth()]],
        ['ddd', weekdaysShort[getDay()]],
        ['DDD', String(dayOfYear)],
        ['SSS', String(getMilliseconds()).padStart(3, '0')],
        ['YY', getYear().toString().slice(-2)],
        ['MM', String(getMonth() + 1).padStart(2, '0')],
        ['Mo', getOrdinal(getMonth() + 1)],
        ['DD', String(getDate()).padStart(2, '0')],
        ['Do', getOrdinal(getDate())],
        ['ww', String(weekOfYear).padStart(2, '0')],
        ['wo', getOrdinal(weekOfYear)],
        ['dd', weekdaysShort[getDay()].slice(0, 2)],
        ['do', getOrdinal(getDay())],
        ['HH', String(getHours()).padStart(2, '0')],
        ['hh', String(getHours() % 12 || 12).padStart(2, '0')],
        ['kk', String(getHours() + 1).padStart(2, '0')],
        ['mm', String(getMinutes()).padStart(2, '0')],
        ['ss', String(getSeconds()).padStart(2, '0')],
        ['SS', String(Math.floor(getMilliseconds() / 10)).padStart(2, '0')],
        ['ZZ', timezoneZZ],
        ['Qo', getOrdinal(quarter)],
        ['Y', getYear().toString()],
        ['M', String(getMonth() + 1)],
        ['D', String(getDate())],
        ['Q', quarter.toString()],
        ['w', String(weekOfYear)],
        ['d', String(getDay())],
        ['E', String(getDay() === 0 ? 7 : getDay())],
        ['H', String(getHours())],
        ['h', String(getHours() % 12 || 12)],
        ['k', String(getHours() + 1)],
        ['m', String(getMinutes())],
        ['s', String(getSeconds())],
        ['S', String(Math.floor(getMilliseconds() / 100))],
        ['A', getHours() >= 12 ? 'PM' : 'AM'],
        ['a', getHours() >= 12 ? 'pm' : 'am'],
        ['Z', timezoneZ],
        ['X', Math.floor(d.getTime() / 1000).toString()],
        ['x', d.getTime().toString()]
    ];

    // Safe token replacement using position tracking
    let result = '';
    let i = 0;

    while (i < format.length) {
        let matched = false;

        // Try to match tokens starting from current position
        for (let j = 0; j < tokenDefinitions.length; j++) {
            const [token, value] = tokenDefinitions[j];

            if (format.substr(i, token.length) === token) {
                result += value;
                i += token.length;
                matched = true;
                break;
            }
        }

        // If no token matched, copy the character as-is
        if (!matched) {
            result += format[i];
            i++;
        }
    }

    return result;
}

/**
 * Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
 */
function getOrdinal(num: number): string {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const value = num % 100;
    const suffix = suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0];
    return num + suffix;
}

export const getVerificationStatus = (status: EVerificationStatus) => {
    switch (status) {
        case EVerificationStatus.APPROVED:
            return 'Approved';
        case EVerificationStatus.PENDING:
            return 'Pending';
        case EVerificationStatus.DECLINED:
            return 'Declined';
        case EVerificationStatus.UNVERIFIED:
            return 'Unverified';
        case EVerificationStatus.ERROR:
            return 'Error';
        default:
            return 'Unknown';
    }
};
